import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCap, TrendingUp, Star } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';

const G = '#0F5A3A';
const GOLD = '#F5B327';
const BTN_R = '8px';

const HERO_IMG = 'https://images.pexels.com/photos/7683887/pexels-photo-7683887.jpeg';

const FLOATING_CARDS = [
  { icon: <GraduationCap size={16} />, label: 'Personalized College Matches', sub: '98% match accuracy' },
  { icon: <TrendingUp size={16} />, label: 'Direct Admissions Opportunities', sub: 'New offers weekly' },
  { icon: <Star size={16} fill={GOLD} />, label: '$28,100+ Scholarships Found', sub: 'For students like you' },
];

export const HeroSection = () => {
  const isMobile = useIsMobile();

  return (
    <section
      style={{ background: G, overflow: 'hidden', position: 'relative', minHeight: isMobile ? 'auto' : '580px' }}
      data-testid="hero-section"
    >
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(245,179,39,0.07) 0%, transparent 55%)', pointerEvents: 'none' }} />

      <div style={{
        maxWidth: '1280px', margin: '0 auto',
        padding: isMobile ? '0 20px' : '0 40px',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '0' : '56px',
        alignItems: 'center',
        minHeight: isMobile ? 'auto' : '580px',
      }}>

        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: 'easeOut' }}
          style={{ padding: isMobile ? '48px 0 40px' : '68px 0', zIndex: 2, position: 'relative' }}
        >
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: GOLD, marginBottom: '20px' }}>
            One Profile. Endless Opportunities.
          </div>

          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '2.2rem' : 'clamp(2.6rem, 4vw, 3.7rem)', fontWeight: 900, lineHeight: 1.08, color: '#fff', letterSpacing: '-0.02em', margin: '0 0 18px' }}>
            Unlock your future.<br />
            One profile opens doors to{' '}
            <span style={{ color: GOLD }}>college, scholarships,<br />and jobs.</span>
          </h1>

          <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.7, marginBottom: '32px', maxWidth: '440px' }}>
            The trusted platform that connects high-achieving students with the right opportunities — personalized for you.
          </p>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '32px' }}>
            <Link to="/signup" data-testid="hero-primary-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '13px 26px', background: GOLD, color: '#1a1a1a', fontWeight: 700, fontSize: '14px', borderRadius: BTN_R, textDecoration: 'none' }}>
              Build Your Profile
            </Link>
            <Link to="/how-it-works" data-testid="hero-secondary-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '13px 26px', border: '1.5px solid rgba(255,255,255,0.55)', color: '#fff', fontWeight: 600, fontSize: '14px', borderRadius: BTN_R, textDecoration: 'none' }}>
              See How It Works
            </Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {[...Array(5)].map((_, i) => <Star key={i} size={13} fill={GOLD} color={GOLD} />)}
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)', marginLeft: '5px' }}>Loved by students</span>
            </div>
            {!isMobile && (
              <>
                <div style={{ width: '1px', height: '18px', background: 'rgba(255,255,255,0.2)' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ display: 'flex' }}>
                    {['#a8d8a8','#7bc47b','#5aad5a'].map((c, i) => (
                      <div key={i} style={{ width: '26px', height: '26px', borderRadius: '50%', background: c, border: '2px solid rgba(255,255,255,0.3)', marginLeft: i > 0 ? '-7px' : 0 }} />
                    ))}
                  </div>
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)' }}>1M+ active students</span>
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* Visual — desktop only */}
        {!isMobile && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.75, delay: 0.12, ease: 'easeOut' }}
            style={{ position: 'relative', height: '520px', display: 'flex', alignItems: 'center' }}
          >
            <div style={{ position: 'absolute', inset: 0, borderRadius: '20px 0 0 20px', overflow: 'hidden' }}>
              <img src={HERO_IMG} alt="Students on campus" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(15,90,58,0.28) 0%, transparent 55%)' }} />
            </div>

            {FLOATING_CARDS.map((card, i) => (
              <motion.div
                key={card.label}
                initial={{ opacity: 0, x: 18 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.55, delay: 0.45 + i * 0.12 }}
                style={{
                  position: 'absolute',
                  left: i === 1 ? '-28px' : '-18px',
                  top: i === 0 ? '14%' : i === 1 ? '47%' : '73%',
                  background: i === 1 ? '#fffde7' : '#fff',
                  borderRadius: '12px',
                  padding: '11px 14px',
                  boxShadow: '0 14px 36px rgba(0,0,0,0.14)',
                  display: 'flex', alignItems: 'center', gap: '9px',
                  minWidth: '210px', zIndex: 3,
                }}
              >
                <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: G, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                  {card.icon}
                </div>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3 }}>{card.label}</div>
                  <div style={{ fontSize: '11px', color: '#777', marginTop: '1px' }}>{card.sub}</div>
                </div>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.85 }}
              style={{ position: 'absolute', bottom: '-14px', right: '16px', background: 'rgba(255,255,255,0.96)', borderRadius: '12px', padding: '11px 18px', boxShadow: '0 10px 30px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: '10px', zIndex: 3 }}
            >
              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: `${G}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUp size={16} color={G} />
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: G }}>$500M+</div>
                <div style={{ fontSize: '11px', color: '#888' }}>Scholarships Connected</div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;
